import { useState, useEffect, useCallback } from 'react';

function supportsDvh() {
  return typeof CSS !== 'undefined'
    && typeof CSS.supports === 'function'
    && CSS.supports('height', '100dvh');
}

function hasVisualViewport() {
  return typeof window !== 'undefined' && typeof window.visualViewport !== 'undefined';
}

/**
 * Resolves the dynamic viewport height the editor sheet needs.
 *
 * On a browser with `dvh`, CSS handles it: variables.css sets `--fgl-vh: 1dvh`
 * behind an @supports rule and nothing here runs. On an older browser we write
 * `--fgl-vh` ourselves from `visualViewport.height`, which is the same number
 * `dvh` exposes — `visualViewport` ships earlier than `dvh` in every engine, so
 * a browser missing the unit still has the API.
 *
 * `supported` is false only when neither exists (iOS 12 and earlier, Chrome 60
 * and earlier), where there is nothing to compute a correct height from.
 */
export function useEditorCapability() {
  const [supported, setSupported] = useState(() => supportsDvh() || hasVisualViewport());
  const [keyboardInset, setKeyboardInset] = useState(0);

  const sync = useCallback(() => {
    const dvh = supportsDvh();
    setSupported(dvh || hasVisualViewport());

    if (!hasVisualViewport()) return;

    const vv = window.visualViewport;

    // Only take over the unit when CSS cannot provide it.
    if (!dvh) {
      document.documentElement.style.setProperty('--fgl-vh', `${vv.height / 100}px`);
    }

    // The on-screen keyboard shrinks the visual viewport without changing the
    // layout viewport. The difference is how far the sheet must lift.
    const inset = Math.max(0, window.innerHeight - vv.height - (vv.offsetTop || 0));
    setKeyboardInset(inset);
  }, []);

  useEffect(() => {
    sync();

    if (!hasVisualViewport()) {
      window.addEventListener('resize', sync);
      return () => window.removeEventListener('resize', sync);
    }

    const vv = window.visualViewport;
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    window.addEventListener('resize', sync);
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
  }, [sync]);

  return { supported, keyboardInset };
}
